import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { Box, Flex, Text } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faPlus,
  faTrash,
  faTriangleExclamation
} from '@fortawesome/pro-regular-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  ButtonText,
  FormFeedback,
  Input,
  OutlineCard,
  Select
} from '@morressier/alembic';
import { declineReviewSchema } from '@publishing-engine/domain/validation';
import { Layout } from 'src/components/Layout';
import { RadioGroup } from 'src/components/RadioGroup';
import { FieldLabel } from 'src/components/Field';
import {
  SUBMISSION_CTX_ID_QUERY_KEY,
  SUBMISSION_ID_QUERY_KEY
} from 'src/constants';
import { useUpdateReviewAdditionalInformation } from 'src/api/mutations';
import { fetchSubmissionContext } from 'src/api/queries';
import { useQuery } from '@tanstack/react-query';
import Loading from '../Status/Loading';
import { useConfirmNavigation } from 'src/hooks/useConfirmNavigation';
import useIsLoggedIn from 'src/hooks/useIsLoggedIn';

type IDeclineReviewFormData = {
  declineReason: string;
  declineReasonList: string;
  recommendReviewers: boolean;
  recommendedReviewers: { email: string }[];
};

const declineReasonList = [
  'Lack of expertise',
  'Conflict of interest',
  'Time constraints',
  'Already reviewing too many manuscripts',
  'Other'
];

const LinkInvalid = () => {
  return (
    <Layout width="960px">
      <Flex flexDirection="column" gap={8}>
        <Flex
          height="112px"
          width="100%"
          background="errorPrimaryFill"
          justifyContent="center"
          alignItems="center"
          borderRadius="base"
          color="actionPrimaryCautionBorderHover"
        >
          <FontAwesomeIcon fontSize={40} icon={faTriangleExclamation} />
        </Flex>
        <Flex flexDirection="column" gap={4} textAlign="center">
          <Text textStyle="headingTitle">Invalid/Expired URL</Text>
          <Text textStyle="textBody">Provided url is invalid or expired.</Text>
        </Flex>
      </Flex>
    </Layout>
  );
};

const InformationSentToEditor = () => {
  const navigate = useNavigate();
  const isLoggedIn = useIsLoggedIn();

  const handleBackToReviews = () => {
    if (isLoggedIn) {
      navigate('/app/reviews');
    } else {
      navigate('/app/home');
    }
  };
  return (
    <Layout width="960px">
      <Flex flexDirection="column" gap={8}>
        <Flex
          height="112px"
          width="100%"
          background="neutralSecondaryFill"
          justifyContent="center"
          alignItems="center"
          borderRadius="base"
          color="neutralSecondaryContent"
        >
          <FontAwesomeIcon fontSize={40} icon={faCheckCircle} />
        </Flex>
        <Flex flexDirection="column" gap={4} textAlign="center">
          <Text textStyle="headingTitle">Information Sent to Editors</Text>
          <Text textStyle="textBody">
            You have successfully sent your information to our editorial team.
            We appreciate your time and expertise in considering this review.
            You may now close this window.
          </Text>
          <Box onClick={handleBackToReviews}>
            <Button>{isLoggedIn ? 'Back to Active Reviews' : 'Log In'}</Button>
          </Box>
        </Flex>
      </Flex>
    </Layout>
  );
};

const DeclineReview = () => {
  const { id: paramId } = useParams();
  const id = paramId || '';
  const searchParams = new URLSearchParams(window.location.search);
  const submissionId = searchParams.get(SUBMISSION_ID_QUERY_KEY) || '';
  const ctxId = searchParams.get(SUBMISSION_CTX_ID_QUERY_KEY) || '';
  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);
  const isLoggedIn = useIsLoggedIn();
  const navigate = useNavigate();

  const navigateAway = () => {
    if (isLoggedIn) {
      navigate('/app/reviews');
    } else {
      navigate('/app/home');
    }
  };
  const methods = useForm<IDeclineReviewFormData>({
    defaultValues: {
      declineReason: '',
      declineReasonList: '',
      recommendReviewers: false,
      recommendedReviewers: [{ email: '' }]
    },
    resolver: zodResolver(declineReviewSchema)
  });

  const { data: submissionContext, isLoading: isLoadingContext } = useQuery({
    queryKey: ['submission-context', ctxId],
    queryFn: () => (ctxId ? fetchSubmissionContext(ctxId) : null)
  });

  const {
    formState: { errors },
    watch,
    control,
    register,
    setValue,
    getValues,
    setError,
    handleSubmit
  } = methods;
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'recommendedReviewers'
  });
  const recommendReviewers = watch('recommendReviewers');
  const declineReasonListValue = watch('declineReasonList');

  const updateAdditionalInformation = useUpdateReviewAdditionalInformation(id);
  useEffect(() => {
    if (!recommendReviewers) {
      setValue('recommendedReviewers', []);
    } else {
      setValue('recommendedReviewers', [{ email: '' }]);
    }
  }, [recommendReviewers, setValue]);

  useConfirmNavigation(
    'Are you sure you want to go back? Changes may not be saved.',
    methods.formState.isDirty && !isFormSubmitted
  );

  if (isLoadingContext) {
    return <Loading />;
  }
  if (!id || !submissionId || !ctxId || !submissionContext) {
    return <LinkInvalid />;
  }

  const removeReviewer = (index: number) => {
    remove(index);

    if (fields.length === 1) {
      append({ email: '' });
    }
  };

  const onSubmit = async () => {
    const formValues = getValues();
    if (formValues.declineReasonList === 'Other' && !formValues.declineReason) {
      setError(
        'declineReason',
        { message: 'Please enter a response to the editor.' },
        {
          shouldFocus: true
        }
      );
      return;
    }

    const declineReason =
      formValues.declineReasonList === 'Other'
        ? formValues.declineReason
        : formValues.declineReasonList;

    const payload = {
      declineReason,
      recommendedReviewers: formValues.recommendReviewers
        ? formValues.recommendedReviewers.map(({ email }) => email)
        : []
    };

    await updateAdditionalInformation.mutateAsync(payload);

    setIsFormSubmitted(true);
  };

  return (
    <>
      {isFormSubmitted ? (
        <InformationSentToEditor />
      ) : (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Flex flexDirection="column">
              <Flex flexDirection="column" gap={16}>
                <Layout width="960px">
                  <Flex flexDirection="column" gap={8}>
                    <Flex
                      height="112px"
                      width="100%"
                      background="neutralSecondaryFill"
                      justifyContent="center"
                      alignItems="center"
                      borderRadius="base"
                      color="neutralSecondaryContent"
                    >
                      <FontAwesomeIcon fontSize={40} icon={faCheckCircle} />
                    </Flex>
                    <Flex flexDirection="column" gap={4}>
                      <Text textStyle="headingTitle">Review Declined</Text>
                      <Text textStyle="textBody">
                        Your decision has been recorded, and we appreciate your
                        time and expertise in considering this review. Before
                        closing, you can also provide a reason for this
                        decision, or suggest other reviewers who might be a good
                        fit.
                      </Text>
                    </Flex>
                    <Flex flexDirection="column" gap={4}>
                      <Flex flexDirection="column">
                        <Text textStyle="headingLine">
                          Reason for Declining
                        </Text>
                        <Text textStyle="textBody">
                          Providing a reason helps us improve future reviewer
                          selections.
                        </Text>
                      </Flex>
                      <Select
                        defaultValue=""
                        width="100%"
                        {...register('declineReasonList')}
                        isInvalid={Boolean(errors.declineReasonList)}
                      >
                        <option disabled value="">
                          Provide a reason
                        </option>
                        {declineReasonList.map((reason, index) => (
                          <option key={index} value={reason}>
                            {reason}
                          </option>
                        ))}
                      </Select>
                      {errors.declineReasonList && (
                        <FormFeedback variant="error">
                          {errors.declineReasonList?.message}
                        </FormFeedback>
                      )}
                      {declineReasonListValue === 'Other' ? (
                        <Input
                          placeholder="Provide a reason"
                          value={watch('declineReason')}
                          {...register('declineReason')}
                          isInvalid={Boolean(errors.declineReason)}
                        />
                      ) : null}
                      {declineReasonListValue === 'Other' &&
                        errors.declineReason && (
                          <FormFeedback variant="error">
                            {errors.declineReason?.message}
                          </FormFeedback>
                        )}
                    </Flex>
                    <Flex flexDirection="column" gap={2}>
                      <Text textStyle="headingLine">
                        Would you like to recommend specific reviewers for this
                        Submission?
                      </Text>
                      <RadioGroup
                        control={control}
                        name="recommendReviewers"
                        options={[
                          {
                            value: false,
                            children: 'No'
                          },
                          {
                            value: true,
                            children: 'Yes'
                          }
                        ]}
                      />
                      {errors.recommendReviewers && (
                        <FormFeedback variant="error">
                          {errors.recommendReviewers?.message}
                        </FormFeedback>
                      )}
                    </Flex>
                    {recommendReviewers ? (
                      <>
                        {fields.map((field, index) => (
                          <OutlineCard borderless width="100%" key={field.id}>
                            <Flex
                              key={field.id}
                              gap={6}
                              padding={8}
                              flexDirection="column"
                              justifyContent="flex-end"
                              backgroundColor="surfacePrimaryFill"
                            >
                              <Flex flexDirection="column" gap={2}>
                                <FieldLabel
                                  label="Professional Email"
                                  subText="Provide the Reviewer's professional email."
                                  isRequired
                                />

                                <Input
                                  placeholder="e.g., fabius@molecularconnections.com"
                                  {...register(
                                    `recommendedReviewers.${index}.email`
                                  )}
                                  invalid={
                                    !!errors.recommendedReviewers?.[index]
                                      ?.email
                                  }
                                />

                                {errors.recommendedReviewers?.[index]
                                  ?.email && (
                                  <FormFeedback variant="error">
                                    {
                                      errors.recommendedReviewers?.[index]
                                        ?.email?.message
                                    }
                                  </FormFeedback>
                                )}
                              </Flex>
                              <Flex justifyContent="flex-end">
                                <ButtonText
                                  variant="secondary"
                                  alignSelf="baseline"
                                  iconLeft={<FontAwesomeIcon icon={faTrash} />}
                                  onClick={() => removeReviewer(index)}
                                >
                                  Delete Reviewer
                                </ButtonText>
                              </Flex>
                            </Flex>
                          </OutlineCard>
                        ))}
                      </>
                    ) : null}
                    {recommendReviewers ? (
                      <Flex justifyContent="flex-end">
                        <ButtonText
                          alignSelf="baseline"
                          iconLeft={<FontAwesomeIcon icon={faPlus} />}
                          onClick={() => append({ email: '' })}
                        >
                          Add Another Reviewer
                        </ButtonText>
                      </Flex>
                    ) : null}
                  </Flex>
                </Layout>
              </Flex>
              <Flex
                paddingY={4}
                paddingX={6}
                bottom={0}
                zIndex={1}
                gap={2}
                justifyContent="space-between"
                position="sticky"
                bgColor="surfacePrimaryFill"
                marginX="auto"
                width="100%"
              >
                <Button variant="caution" onClick={navigateAway}>
                  Exit
                </Button>
                <Flex gap={2}>
                  {isLoggedIn ? (
                    <Button
                      variant="secondary"
                      type="button"
                      onClick={navigateAway}
                    >
                      {isLoggedIn ? 'Back to Active Reviews' : 'Log In'}
                    </Button>
                  ) : null}
                  <Button variant="primary" type="submit">
                    Submit
                  </Button>
                </Flex>
              </Flex>
            </Flex>
          </form>
        </FormProvider>
      )}
    </>
  );
};

export default DeclineReview;
